import { createElement, type CSSProperties, type ElementType, type HTMLAttributes } from 'react'
import { cx } from './utils/cx'

export type TextVariant =
  | 'display'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'body-lg'
  | 'body'
  | 'body-sm'
  | 'caption'
  | 'micro'
  | 'mono'
  | 'mono-sm'
  | 'overline'
  | 'tag'
  // SnapTransfer extension variants — owned by components.css under
  // .st-type.mono-label / .mono-caption / .mono-micro. Distinct mono-stamp
  // treatments the reference library doesn't ship; used for form labels,
  // chip captions, and micro meta across the app.
  | 'mono-label'
  | 'mono-caption'
  | 'mono-micro'

// Semantic color tokens. The `color` prop also accepts any raw CSS color
// string (e.g. 'var(--hot)', '#123') for one-off needs — the value is
// applied inline when it doesn't match a semantic name.
export type TextColor = 'ink' | 'mute' | 'quiet' | 'paper' | 'hot'
const SEMANTIC_COLORS = new Set<string>(['ink', 'mute', 'quiet', 'paper', 'hot'])

const VARIANT_TAG: Record<TextVariant, ElementType> = {
  display: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  'body-lg': 'p',
  body: 'p',
  'body-sm': 'p',
  caption: 'span',
  micro: 'span',
  mono: 'span',
  'mono-sm': 'span',
  overline: 'span',
  tag: 'span',
  'mono-label': 'span',
  'mono-caption': 'span',
  'mono-micro': 'span'
}

interface TextProps extends HTMLAttributes<HTMLElement> {
  variant?: TextVariant
  weight?: 400 | 500 | 600 | 700
  align?: 'left' | 'center' | 'right'
  color?: TextColor | (string & {})
  opacity?: number
  truncate?: boolean
  nowrap?: boolean
  balance?: boolean
  as?: ElementType
}

export function Text({
  variant = 'body',
  weight,
  align,
  color,
  opacity,
  truncate,
  nowrap,
  balance,
  className,
  style,
  as,
  children,
  ...rest
}: TextProps) {
  const Tag = as || VARIANT_TAG[variant] || 'p'
  const isSemanticColor = typeof color === 'string' && SEMANTIC_COLORS.has(color)
  const rawColor = !isSemanticColor && color !== undefined ? color : undefined
  const merged: CSSProperties | undefined =
    opacity != null || rawColor
      ? {
          ...style,
          ...(rawColor ? { color: rawColor } : null),
          ...(opacity != null ? { opacity } : null)
        }
      : style
  return createElement(
    Tag,
    {
      className: cx(
        'st-type',
        variant,
        weight && 'w-' + weight,
        align && 'align-' + align,
        isSemanticColor && 'c-' + color,
        truncate && 'truncate',
        nowrap && 'nowrap',
        balance && 'balance',
        className
      ),
      style: merged,
      ...rest
    },
    children
  )
}
